// The customer-facing side of Deploy Demo: a shareable, anonymous preview of a Studio flow, opened from a
// link like /demo?key=mk_demo_<tenant>.<rand>. No login, ever — the token in the URL is what authorizes the
// visitor, and taking the demo down revokes it server-side.
//
// One route serves every tenant's demos, so nothing is known at build time: the token is resolved against
// the public /runtime/demo endpoint, and the flow's TYPE decides which interface renders. A quote bot gets
// the real quote/booking card UI (the same EcapChatPage that serves R3's customers); any other workflow
// gets the same chat with the generic component renderer, since only quote-specific screens are quote-only.
import React, { useEffect, useState } from 'react';
import EcapChatPage from './EcapChatPage';
import { getDemoInfo } from '../services/ecapApi';

/** The demo token from ?key= — the same slot the chat page already reads, so it flows straight through. */
function resolveToken() {
  try {
    return (new URLSearchParams(window.location.search).get('key') || '').trim();
  } catch (e) {
    return '';
  }
}

function Centered({ title, detail }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-6">
      <div className="max-w-sm text-center">
        <h1 className="text-lg font-semibold text-body mb-2">{title}</h1>
        {detail && <p className="text-13 text-body/60 leading-relaxed">{detail}</p>}
      </div>
    </div>
  );
}

export default function DemoPage() {
  const token = resolveToken();
  // 'loading' → 'ready' (meta resolved) | 'gone' (no token, unknown token, or taken down).
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    if (!token) {
      setState({ status: 'gone' });
      return undefined;
    }
    getDemoInfo(token)
      .then((meta) => { if (alive) setState({ status: 'ready', meta }); })
      .catch(() => { if (alive) setState({ status: 'gone' }); });
    return () => { alive = false; };
  }, [token]);

  if (state.status === 'loading') {
    return <Centered title="Loading…" />;
  }
  if (state.status === 'gone') {
    return (
      <Centered
        title="This demo link is no longer available."
        detail="The demo may have been taken down. Ask whoever shared it for an up-to-date link."
      />
    );
  }

  const { meta } = state;
  const isQuoteBot = meta.flowType === 'QUOTE';
  return (
    <EcapChatPage
      cfg={{
        // The demo token rides the flow-key slot: the runtime accepts it as an alternative credential, so
        // the chat page needs no demo-specific plumbing and stays pinned to the deployed snapshot.
        flowKey: token,
        brand: { subtitle: (meta.flowName || 'Demo').toUpperCase() },
        // Quote bots get the quote/booking cards; every other workflow falls through to the generic
        // component renderer rather than inheriting quote-specific screens.
        components: isQuoteBot ? undefined : {},
      }}
      onLogout={null}
    />
  );
}
